import { Skeleton } from "antd";
import { useTranslation } from "react-i18next";
import PageTitle from "../../page-title";

/**
 * The template gallery's loading state, rendered HOST-side.
 *
 * Deliberately a replica of the Studio gallery's own `LoadingState`
 * (`widgets/template-gallery/TemplatesPage.tsx` in the Studio repo), down to
 * the card count, the 4:3 tiles and the `lg:grid-cols-3` tier — same reasoning
 * as the editor's spinner in `TemplateStudioFrame`: the templates route shows
 * up to three host loading states in a row (lazy chunk → business-type
 * resolution → iframe boot) and then hands off to the Studio's own. Different
 * shapes read as three page loads; one shape reads as a single continuous wait.
 *
 * The heading is REAL text, not a bar, because the Studio paints the same
 * `PageTitle` with the same two strings the moment it mounts — the two
 * components are byte-identical ports. Skeletoning it would mean animating
 * something we already know, then swapping it for identical text.
 *
 * The guide button IS a bar: rendering a real one host-side would put a
 * non-functional control on screen for the length of the load.
 *
 * Keep this in step with the Studio's `LoadingState`. It drifting is a
 * cosmetic bug, not a broken page — the handoff just stops being invisible.
 */
export default function TemplateGallerySkeleton({
  picker = false,
}: {
  /**
   * The frame is running the gallery as a PICKER (`?mode=select`), where the
   * Studio paints no page heading and no guide button — the host's own step
   * chrome supplies both.
   *
   * Without this the replica promises a title the real gallery never renders,
   * so the merchant reads "Store Templates / Pick a professionally designed
   * template…" directly under the wizard's own heading and then watches it
   * disappear. Exactly the drift this component's doc warns about, and it
   * arrived the day picker mode did (Milan, 2026-09-18).
   */
  picker?: boolean;
} = {}) {
  const { t } = useTranslation();

  return (
    // Matches the Studio's embedded wrapper. Neither adds page padding: the
    // dashboard's route content column already supplies it, so a second inset
    // here would shift the skeleton relative to the frame it precedes.
    <div className="max-w-6xl mx-auto">
      {!picker && (
        <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-3 sm:gap-4 mb-8">
          <PageTitle
            title={t("templates.templatesPage.title")}
            description={t("templates.templatesPage.description")}
          />
          <Skeleton.Button active size="small" style={{ width: 128 }} />
        </div>
      )}

      <div className={picker ? "" : "mt-6"}>
        <div
          className="rounded-xl border"
          style={{
            borderColor: "var(--color-border)",
            background: "var(--color-surface-elevated)",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          <div
            className="px-6 py-5 border-b"
            style={{ borderColor: "var(--color-border)" }}
          >
            <Skeleton active paragraph={{ rows: 1 }} title={{ width: "40%" }} />
          </div>
          {/* Three across from `md` in picker mode, matching the Studio: the
              onboarding frame is ~780px and never reaches `lg`, so keeping
              `lg` here would hand off from a two-column skeleton to a
              three-column gallery. */}
          <div
            className={`p-6 grid grid-cols-1 sm:grid-cols-2 gap-5 ${
              picker ? "md:grid-cols-3" : "lg:grid-cols-3"
            }`}
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="rounded-xl overflow-hidden"
                style={{
                  border: "1px solid var(--color-border)",
                  background: "var(--color-surface-elevated)",
                }}
              >
                <div
                  className="aspect-[4/3] animate-pulse"
                  style={{ background: "var(--color-surface)" }}
                />
                <div className="p-4">
                  <Skeleton
                    active
                    paragraph={{ rows: 2 }}
                    title={{ width: "60%" }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
